// ---------------------------------------------------------------------------
// stepper-sidebar.tsx — Editorial-minimalism step sidebar.
// Large outlined step numerals anchor the composition. Active/done/upcoming
// states use high-contrast typography — no progress bars or shadows.
// ---------------------------------------------------------------------------

import React from 'react'
import { CogoLogoBrand } from '@/components/cogo-logo'
import { useStudioLocale } from '@/i18n'
import styles from './new-project-wizard-page.module.css'

/** The four step numerals, paired with the dictionary key that names each.
 *  Numbers and keys here; words in `studio-new-project-copy.ts`. */
const STEP_KEYS = [
  { num: 1, key: 'start' },
  { num: 2, key: 'style' },
  { num: 3, key: 'shape' },
  { num: 4, key: 'create' },
] as const

interface StepperSidebarProps {
  currentStep: 1 | 2 | 3 | 4
}

export function StepperSidebar({ currentStep }: StepperSidebarProps) {
  const copy = useStudioLocale().strings.newProject
  return (
    <aside className={styles.sidebar}>
      <div className={styles.sidebarBrand}>
        <CogoLogoBrand size={28} variant="speed" />
        <div className={styles.sidebarBrandCopy}>
          <span className={styles.sidebarTitle}>{copy.sidebarTitle}</span>
          <span className={styles.sidebarSubtitle}>cogo studio</span>
        </div>
      </div>

      <nav
        className={styles.stepList}
        aria-label={copy.stepsNav}
        data-testid="new-project-stepper"
      >
        {STEP_KEYS.map((step) => {
          const isDone = currentStep > step.num
          const isActive = currentStep === step.num
          const numStr = String(step.num).padStart(2, '0')
          const { label, sublabel } = copy.steps[step.key]

          return (
            <div
              key={step.num}
              data-testid={`new-project-step-${step.num}`}
              className={[
                styles.stepItem,
                isDone ? styles.stepDone : '',
                isActive ? styles.stepActive : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className={styles.stepNumeral} aria-hidden="true">
                {numStr}
              </span>
              <div className={styles.stepMeta}>
                <span className={styles.stepLabel}>{label}</span>
                <span className={styles.stepSublabel}>{sublabel}</span>
              </div>
              {isDone && (
                <svg
                  className={styles.stepCheck}
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M3 8l3.5 3.5L13 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          )
        })}
      </nav>

      <div className={styles.sidebarFooter}>
        <p className={styles.sidebarNote}>{copy.unsavedNote}</p>
      </div>
    </aside>
  )
}
